<template>
  <v-container>
    <v-row>
      <v-col>
        <v-btn color="primary" @click="openModal('createGroup')">Create Criteria Group</v-btn>
      </v-col>
    </v-row>
    <draggable
      v-model="criteriaGroups"
      item-key="sequence"
      :group="{ name: 'criteriaGroups', pull: 'clone', put: false }"
      :animation="200"
      :ghostClass="'ghost'"
      :forceFallback="true"
      filter=".ignore-drag"
      @end="onDragEnd">
      <template #item="{ element }">
        <v-row>
          <v-col cols="12" md="1">
            <v-icon class="cursor-move">mdi-drag</v-icon>
          </v-col>
          <v-col cols="12" md="6">{{ element.group_name }}</v-col>
          <v-col>
            <v-btn icon variant="text" small color="primary" @click="openModal('edit', element)">
              <v-icon icon="mdi-pencil"></v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              :color="element.thubmnail ? 'primary' : 'secondary'"
              @click="openModal('editThumbnail', element)">
              <v-icon icon="mdi-file-arrow-up-down"></v-icon>
            </v-btn>
            <v-btn icon variant="text" color="primary" @click="openModal('delete', element)">
              <v-icon icon="mdi-delete"></v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </draggable>

    <v-dialog v-model="isCreateGroupModalOpen" max-width="500px">
      <CriteriaGroupEditComponent
        :yearId="yearId"
        :criteriaGroup="new CriteriaGroupDTO()"
        :isCreate="true"
        @criteriaGroupEditComplete="closeModal"
        @criteriaGroupEditCanceled="closeModal"
        @snack="throwSnack" />
      />
    </v-dialog>
    <v-dialog v-model="isEditGroupModelOpen" max-width="500px">
      <CriteriaGroupEditComponent
        :yearId="yearId"
        :criteriaGroup="selectedGroup"
        :isCreate="false"
        @criteriaGroupEditComplete="closeModal"
        @criteriaGroupEditCanceled="closeModal"
        @snack="throwSnack" />
      />
    </v-dialog>
    <v-dialog v-model="isEditGroupAssetOpen">
      <AssetPreviewComponent
        :asset="selectedGroup.thubmnail"
        type="image"
        :onUpload="handleFileUpload"
        @assetSaved="handleAssetSaved"
        @assetCanceled="closeModal"
        @snack="throwSnack" />
    </v-dialog>
    <ConfirmDeleteComponent
      :isOpen="isDeleteGroupModelOpen"
      item="Criteria Group"
      description="This will remove the criteria group and unassign any criteria associated to it."
      @actionConfirmed="handleDeleteConfirmed"
      @actionCanceled="closeModal" />
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  CriteriaGroupDTO,
  IAssetDTO,
  ICriteriaGroupDTO,
  IPaginationDTO,
  PaginationDTO,
} from '@/dtos';
import type { SortableEvent } from 'sortablejs';
import { useCriteriaStore, useUpdateStatusStore } from '@/stores/modules';
import CriteriaGroupEditComponent from './CriteriaGroupEditComponent.vue';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import AssetPreviewComponent from '../ui/AssetPreviewComponent.vue';
import { uploadCriteriaGroupAsset } from '@/controllers/admin';
import ConfirmDeleteComponent from '../ui/ConfirmDeleteComponent.vue';
import draggable from 'vuedraggable';
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);
interface Props {
  yearId: string;
}
const props = defineProps<Props>();
const yearId = computed(() => props.yearId);
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const criteriaStore = useCriteriaStore();
const statusStore = useUpdateStatusStore();
const { data: query } = criteriaStore.getCriteriaGroupsForYearQuery(yearId, pagination);
const criteriaGroups = ref<ICriteriaGroupDTO[]>([]);
const isCreateGroupModalOpen = ref<boolean>(false);
const isEditGroupModelOpen = ref<boolean>(false);
const isEditGroupAssetOpen = ref<boolean>(false);
const isDeleteGroupModelOpen = ref<boolean>(false);
const selectedGroup = ref<ICriteriaGroupDTO>(new CriteriaGroupDTO());

watch(
  () => query.value?.data,
  () => {
    if (query.value?.data) {
      criteriaGroups.value = query.value.data.map((group) => new CriteriaGroupDTO(group));
    } else {
      criteriaGroups.value = [];
    }
  },
  { immediate: true },
);

const handleFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadCriteriaGroupAsset(selectedGroup.value.id, file, updateProgress);
  return response;
};

const handleAssetSaved = async () => {
  await criteriaStore.invalidateCriteriaGroups();
};
const handleDeleteConfirmed = async () => {
  criteriaStore.removeCriteriaGroup.mutateAsync(selectedGroup.value.id);
  criteriaStore.invalidateCriteriaGroups();
};

const key = computed(() => `criteriaGroup-${selectedGroup.value.id}`);
const status = computed(() => statusStore.getStatus(key.value));
watch(status, (value) => {
  if (!value) return;
  emit('snack', 'Criteria Group', value, statusStore.getError(key.value));
  closeModal();
});

const closeModal = async () => {
  isCreateGroupModalOpen.value = false;
  isEditGroupModelOpen.value = false;
  isEditGroupAssetOpen.value = false;
  isDeleteGroupModelOpen.value = false;
  selectedGroup.value = new CriteriaGroupDTO();
};
const openModal = async (type: string, item?: ICriteriaGroupDTO) => {
  if (item) {
    selectedGroup.value = item;
  }
  if (type === 'createGroup') {
    isCreateGroupModalOpen.value = true;
  } else if (type === 'edit' && item) {
    isEditGroupModelOpen.value = true;
  } else if (type === 'editThumbnail' && item) {
    isEditGroupAssetOpen.value = true;
  } else if (type === 'delete' && item) {
    isDeleteGroupModelOpen.value = true;
  }
};

function onDragEnd(evt: SortableEvent) {
  if (evt.oldIndex === evt.newIndex) {
    return;
  }

  criteriaGroups.value.forEach((group, index) => {
    group.sequence = index + 1;
  });
  criteriaStore.updateCriteriaGroups.mutateAsync({
    yearId: yearId.value,
    groups: criteriaGroups.value,
  });
}
</script>
